import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { fmtCPF, maskCPF, fmtDateTime } from '../utils/formatters';
import PropostaPadrao, { VarianteDoDocumento } from './proposta/PropostaPadrao';
import { BotoesFlutuantes } from '../components/shared/BotoesFlutuantes';
import { podeDecidirNoLink, fraseDoLinkSemDecisao } from '../utils/crm';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';


const REJECTION_REASONS = [
  'Preço acima do orçamento',
  'Escolheu outro fornecedor',
  'Não é o momento',
  'Escopo não atende',
  'Prazo não atende',
  'Outro'
];

export default function ProposalPublicView() {
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const [proposal, setProposal] = useState<any>(null);
  // Auto-altura do iframe do documento HTML externo (o doc manda a altura via postMessage)
  const [htmlDocHeight, setHtmlDocHeight] = useState<number>(0);
  const [viewportW, setViewportW] = useState<number>(window.innerWidth);
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const h = e?.data?.proposalDocHeight;
      if (typeof h === 'number' && h > 200) setHtmlDocHeight(h);
    };
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener('message', onMsg);
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('message', onMsg); window.removeEventListener('resize', onResize); };
  }, []);
  // Documento HTML externo vira a PÁGINA, sem iframe: no iOS o iframe aplica
  // zoom próprio e quebra a escala; em top-level o viewport do documento vale,
  // e o backend injeta os botões de aceite e recusa direto nele.
  //
  // O documento é ESCRITO nesta página em vez de redirecionar: assim o cliente
  // continua vendo hub.ipcom.app.br/p/CODIGO na barra, e não o endereço do
  // servidor (pedido do Heitor, 06/09/2026). O document.write executa os
  // scripts do documento, que é o que faz os botões de aceite funcionarem; os
  // endpoints deles são absolutos, justamente por causa disto. Se a busca
  // falhar, cai no redirect de antes, que sempre funcionou.
  useEffect(() => {
    if (!proposal?.hasCustomHtml || !proposal?.code) return;
    let cancelado = false;
    const url = `${API_URL}/proposals/public/${proposal.code}/document`;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(url, { cache: 'no-store' });
        if (!r.ok) throw new Error(String(r.status));
        const html = await r.text();
        if (cancelado) return;
        document.open();
        document.write(html);
        document.close();
      } catch {
        if (!cancelado) window.location.replace(url);
      }
    }, 600); // dá tempo do PATCH /view registrar a visualização
    return () => { cancelado = true; clearTimeout(t); };
  }, [proposal]);
  const DOC_DESIGN_W = 1180;
  const docScale = viewportW < DOC_DESIGN_W ? viewportW / DOC_DESIGN_W : 1;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [baixandoPdf, setBaixandoPdf] = useState(false);

  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const [acceptData, setAcceptData] = useState({ name: '', email: '', cpf: '', role: '' });
  const [rejectData, setRejectData] = useState({ name: '', email: '', reason: '', notes: '' });

  useEffect(() => { loadData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, searchParams]);

  // Os dados da empresa e o template eram lidos aqui para o cabecalho antigo
  // da proposta padrao. As quatro folhas da v1.7 trazem a marca no desenho, e
  // ninguem mais consome esses dois fetches.
  const loadData = async () => { await loadProposal(); };

  const loadProposal = async () => {
    try {
      const res = await fetch(API_URL + '/proposals/public/' + code);
      if (!res.ok) throw new Error('Proposta nao encontrada');
      const data = await res.json();
      setProposal(data);
      
      const isPreview = searchParams.get('preview') === 'true';
      if (data.status === 'Enviada' && !isPreview) {
        await fetch(API_URL + '/proposals/public/' + code + '/view', { method: 'PATCH' });
      }
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar proposta');
    }
    setLoading(false);
  };


  const handleAcceptSubmit = async () => {
    if (!acceptData.name || !acceptData.email || !acceptData.cpf) {
      alert('Preencha nome, email e CPF'); return;
    }
    if (acceptData.cpf.replace(/\D/g, '').length !== 11) {
      alert('CPF inválido'); return;
    }
    setActionLoading(true);
    try {
      const res = await fetch(API_URL + '/proposals/public/' + code + '/accept', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(acceptData)
      });
      if (!res.ok) throw new Error('Erro ao aceitar');
      await loadProposal();
      setShowAcceptModal(false);
      alert('Proposta aceita com sucesso!');
    } catch (e) { alert('Erro ao aceitar proposta'); }
    setActionLoading(false);
  };

  const handleRejectSubmit = async () => {
    if (!rejectData.reason) { alert('Selecione um motivo'); return; }
    setActionLoading(true);
    try {
      const res = await fetch(API_URL + '/proposals/public/' + code + '/reject', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(rejectData)
      });
      if (!res.ok) throw new Error('Erro ao recusar');
      await loadProposal();
      setShowRejectModal(false);
    } catch (e) { alert('Erro ao recusar proposta'); }
    setActionLoading(false);
  };

  /**
   * QUAL VARIANTE DO DOCUMENTO MOSTRAR.
   *
   * A ordem é: o campo gravado na proposta manda; o parâmetro de URL só serve
   * para PRÉVIA, enquanto o campo não existe no backend.
   *
   * ⚠️ Enquanto for só o parâmetro, o link CRU abre a versão de voz. Para
   * cliente de WhatsApp isso é o documento errado, então a prévia serve para
   * decidir o texto, não para enviar. Quando `templateVariant` chegar na API,
   * esta função passa a responder certo sozinha e o parâmetro vira só override
   * de quem está editando.
   *
   * Nunca deduzir do carrinho: PRO20260085 tem 50 combos de WhatsApp E 10 canais
   * de voz, e qualquer regra por item escolheria por conta própria.
   */
  const varianteDaProposta = (p: any, param: string | null): VarianteDoDocumento => {
    const gravada = String(p?.templateVariant || '').toLowerCase();
    if (gravada === 'whatsapp' || gravada === 'voz') return gravada as VarianteDoDocumento;
    return String(param || '').toLowerCase() === 'whatsapp' ? 'whatsapp' : 'voz';
  };
  const variante = varianteDaProposta(proposal, searchParams.get('variante'));

  // A rota /pdf responde `attachment` (nome do arquivo com o código e o
  // cliente): o navegador salva e a página fica onde está. `window.open` abria
  // uma aba em branco só para fechar logo depois.
  const urlDoPdf = () => {
    const templateId = searchParams.get('template');
    return templateId
      ? API_URL + '/proposals/public/' + proposal?.code + '/pdf?templateId=' + templateId
      : API_URL + '/proposals/public/' + proposal?.code + '/pdf';
  };
  const handleDownloadPDF = () => {
    setBaixandoPdf(true);
    window.location.href = urlDoPdf();
    // O PDF é gerado pelo Chrome do backend e leva alguns segundos.
    setTimeout(() => setBaixandoPdf(false), 6000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-500">Carregando proposta...</div>
      </div>
    );
  }

  if (error || !proposal) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Proposta não encontrada</h1>
          <p className="text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (proposal.hasCustomHtml) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#eef2f7' }}>
        <div className="text-gray-500">Abrindo proposta…</div>
      </div>
    );
  }

  const whatsappNumber = '554531225150';
  const whatsappMessage = encodeURIComponent(`Olá! Vi a proposta ${proposal.code} e gostaria de mais informações.`);
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  const hasExternalDoc = !!(proposal.hasCustomHtml || proposal.customPdfUrl);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Baixar PDF + WhatsApp: o mesmo par flutuante da página pública de
          material (components/shared/BotoesFlutuantes), já com print:hidden —
          o PDF da proposta é esta página impressa pelo Chrome, e `fixed`
          repetiria os botões em TODA folha (o defeito do #175). */}
      <BotoesFlutuantes
        baixar={{ rotulo: 'Baixar PDF', href: urlDoPdf() }}
        baixando={baixandoPdf}
        onBaixar={handleDownloadPDF}
        whatsappUrl={whatsappUrl}
      />

      {/* PROPOSTA PADRAO — quatro folhas A4 na linguagem da v1.7 (ver
          pages/proposta/PropostaPadrao.tsx). Fica FORA do container estreito
          porque a folha ja tem largura e fundo proprios. */}
      {!hasExternalDoc && <PropostaPadrao proposal={proposal} variante={variante} />}

      {/* `print:empty:hidden`: vazio (proposta padrão, sem quadro de aceite),
          este bloco de altura zero ainda abria uma FOLHA EM BRANCO no fim do
          PDF, logo depois de uma folha cheia. Com conteúdo ele imprime. */}
      <div className="max-w-4xl mx-auto pb-28 print:pb-0 print:empty:hidden">

        {/* PDF EXTERNO — o documento (ex.: proposta desenhada) É a apresentação.
            Páginas renderizadas como imagem = documento corrido limpo (sem o
            viewer nativo do browser); iframe só como fallback. */}
        {hasExternalDoc && (
          <div className="mb-2">
            {proposal.hasCustomHtml ? (
              <div style={{ height: htmlDocHeight ? `${htmlDocHeight * docScale}px` : '100vh', overflow: 'hidden' }}>
                <iframe
                  src={`${API_URL}/proposals/public/${proposal.code}/document`}
                  title={`Proposta ${proposal.code}`}
                  style={{
                    width: docScale < 1 ? `${DOC_DESIGN_W}px` : '100%',
                    height: htmlDocHeight ? `${htmlDocHeight}px` : '100vh',
                    border: 'none',
                    transform: docScale < 1 ? `scale(${docScale})` : undefined,
                    transformOrigin: 'top left',
                  }}
                  scrolling={htmlDocHeight ? 'no' : 'auto'}
                />
              </div>
            ) : Array.isArray(proposal.customPdfPageUrls) && proposal.customPdfPageUrls.length > 0 ? (
              <div className="space-y-2">
                {proposal.customPdfPageUrls.map((url: string, idx: number) => (
                  <img
                    key={idx}
                    src={url}
                    alt={`Proposta ${proposal.code} — página ${idx + 1}`}
                    className="w-full bg-white shadow-sm"
                    loading={idx > 1 ? 'lazy' : undefined}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-white shadow-sm overflow-hidden px-2 sm:px-6 py-4">
                <iframe
                  src={proposal.customPdfUrl}
                  title={`Proposta ${proposal.code}`}
                  className="w-full rounded-lg border border-gray-200"
                  style={{ height: '82vh' }}
                />
                <div className="mt-3 text-center">
                  <a href={proposal.customPdfUrl} target="_blank" rel="noopener noreferrer"
                    className="text-sm text-orange-600 font-semibold hover:underline">
                    📄 Abrir a proposta em tela cheia
                  </a>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Comprovante de aceite/recusa. O corpo da proposta padrão saiu daqui
            para PropostaPadrao; o que sobra é o registro da decisão, que vale
            para as duas vias (padrão e documento externo). */}
        {(proposal.status === 'Aceita' || proposal.status === 'Recusada') && (
        <div className="bg-white shadow-sm max-w-3xl mx-auto my-4 rounded-xl">
          <div className="px-4 sm:px-8 py-6">

            {/* Botões de ação ficam na barra flutuante (padrão unificado com o doc externo) */}

            {/* Status Aceita */}
            {proposal.status === 'Aceita' && (
              <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="text-center text-green-700 font-semibold mb-3">✓ Proposta Aceita</div>
                <div className="text-sm text-green-800 space-y-1">
                  <p><strong>Data:</strong> {fmtDateTime(proposal.acceptedAt)}</p>
                  {proposal.acceptedByName && <p><strong>Nome:</strong> {proposal.acceptedByName}</p>}
                  {proposal.acceptedByEmail && <p><strong>Email:</strong> {proposal.acceptedByEmail}</p>}
                  {proposal.acceptedByCpf && <p><strong>CPF:</strong> {fmtCPF(proposal.acceptedByCpf)}</p>}
                  {proposal.acceptedByRole && <p><strong>Cargo:</strong> {proposal.acceptedByRole}</p>}
                </div>
              </div>
            )}

            {/* Status Recusada */}
            {proposal.status === 'Recusada' && (
              <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="text-center text-red-700 font-semibold mb-3">✕ Proposta Recusada</div>
                <div className="text-sm text-red-800 space-y-1">
                  <p><strong>Data:</strong> {fmtDateTime(proposal.rejectedAt)}</p>
                  {proposal.rejectedByName && <p><strong>Nome:</strong> {proposal.rejectedByName}</p>}
                  {proposal.rejectedByEmail && <p><strong>Email:</strong> {proposal.rejectedByEmail}</p>}
                  {proposal.rejectionReason && <p><strong>Motivo:</strong> {proposal.rejectionReason}</p>}
                  {proposal.rejectionNotes && <p><strong>Observações:</strong> {proposal.rejectionNotes}</p>}
                </div>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Barra flutuante de ações — só Aceitar/Recusar (sem download; WhatsApp fica sozinho no canto).
          P6 (11/10/2026): só em Enviada/Visualizada e dentro da validade. Antes oferecia também em Rascunho e ignorava
          a validade; a proposta vencida ou ainda não enviada diz por quê, em vez de oferecer o aceite. */}
      {!podeDecidirNoLink(proposal) && fraseDoLinkSemDecisao(proposal) && (
        <div className="print:hidden fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full bg-white shadow-xl border border-gray-200 text-sm text-gray-700" data-testid="link-sem-decisao">
          {fraseDoLinkSemDecisao(proposal)}
        </div>
      )}
      {podeDecidirNoLink(proposal) && (
        <div className="print:hidden fixed bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center justify-center gap-2 sm:gap-3 flex-wrap px-2 w-full sm:w-auto">
          <button onClick={() => setShowAcceptModal(true)} disabled={actionLoading}
            className="px-4 py-2 text-sm sm:px-7 sm:py-3 sm:text-base bg-success text-white font-bold rounded-full shadow-xl hover:bg-success-strong hover:scale-105 transition-all disabled:opacity-50">
            ✓ Aceitar proposta
          </button>
          <button onClick={() => setShowRejectModal(true)} disabled={actionLoading}
            className="px-4 py-2 text-sm sm:px-7 sm:py-3 sm:text-base bg-danger text-white font-bold rounded-full shadow-xl hover:bg-danger-strong hover:scale-105 transition-all disabled:opacity-50">
            ✕ Recusar
          </button>
        </div>
      )}

      {/* Modal Aceite */}
      {showAcceptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Confirmar Aceite</h2>
            <p className="text-sm text-gray-600 mb-4">Para aceitar esta proposta, preencha seus dados:</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo *</label>
                <input type="text" value={acceptData.name} onChange={(e) => setAcceptData({ ...acceptData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Seu nome completo" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" value={acceptData.email} onChange={(e) => setAcceptData({ ...acceptData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="seu@email.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CPF *</label>
                <input type="text" value={acceptData.cpf} onChange={(e) => setAcceptData({ ...acceptData, cpf: maskCPF(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="000.000.000-00" maxLength={14} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cargo (opcional)</label>
                <input type="text" value={acceptData.role} onChange={(e) => setAcceptData({ ...acceptData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500" placeholder="Ex: Diretor, Gerente..." />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowAcceptModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" disabled={actionLoading}>Cancelar</button>
              <button onClick={handleAcceptSubmit} disabled={actionLoading} className="flex-1 px-4 py-2 bg-success text-white font-bold rounded-lg hover:bg-success-strong disabled:opacity-50">
                {actionLoading ? 'Enviando...' : 'Confirmar Aceite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Recusa */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Recusar Proposta</h2>
            <p className="text-sm text-gray-600 mb-4">Por favor, informe o motivo da recusa:</p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo da recusa *</label>
                <select value={rejectData.reason} onChange={(e) => setRejectData({ ...rejectData, reason: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500">
                  <option value="">Selecione um motivo...</option>
                  {REJECTION_REASONS.map((r) => (<option key={r} value={r}>{r}</option>))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome (opcional)</label>
                <input type="text" value={rejectData.name} onChange={(e) => setRejectData({ ...rejectData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" placeholder="Seu nome" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email (opcional)</label>
                <input type="email" value={rejectData.email} onChange={(e) => setRejectData({ ...rejectData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" placeholder="seu@email.com" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observações (opcional)</label>
                <textarea value={rejectData.notes} onChange={(e) => setRejectData({ ...rejectData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500" rows={3} placeholder="Informações adicionais..." />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowRejectModal(false)} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50" disabled={actionLoading}>Cancelar</button>
              <button onClick={handleRejectSubmit} disabled={actionLoading} className="flex-1 px-4 py-2 bg-danger text-white font-bold rounded-lg hover:bg-danger-strong disabled:opacity-50">
                {actionLoading ? 'Enviando...' : 'Confirmar Recusa'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
